// Web port of the desktop app's StreamNookBadge tier card.
//
// Source: C:\StreamNook\src\components\StreamNookBadge.tsx
// The cypher / matrix-decode reveal animation is stripped — that's a
// "discovering someone else's tier" surface in chat, and this card is always
// rendered on the user's OWN profile page (skipCypher equivalent).

import { memo } from 'react';

interface TierInfo {
  label: string | null;
  numberClassName: string;
  hairlineClassName: string;
  labelClassName: string;
  auraClassName?: string;
}

const NUMBER_BASE_CLASS =
  "font-['Fraunces_Variable'] italic font-light text-[52px] tabular-nums leading-none tracking-tight";

const LABEL_BASE_CLASS =
  'text-[10px] uppercase tracking-[0.28em] font-semibold leading-none';

// Easter eggs: a number with cultural/meme significance. A plain-string entry
// keeps the tier styling and only swaps the label text. An object entry can
// also override the number / hairline / label styling for a fully custom look.
// Mirrors the desktop app's EASTER_EGGS map.
type EasterEgg = string | ({ label: string } & Partial<Omit<TierInfo, 'label'>>);

const EASTER_EGGS: Record<number, EasterEgg> = {
  42: 'The Answer',
  67: {
    label: 'Six Seven',
    numberClassName: `${NUMBER_BASE_CLASS} text-lime-300`,
    hairlineClassName: 'bg-gradient-to-r from-transparent via-lime-400/40 to-transparent',
    labelClassName: `${LABEL_BASE_CLASS} text-lime-300`,
  },
  69: 'Nice',
  141: 'Going Dark',
  404: 'Not Found',
  420: 'Blaze It',
  1337: 'Leet',
  9000: 'Over 9000',
};

const getTierBase = (n: number): TierInfo => {
  if (n === 1) {
    return {
      label: 'Ethereal',
      numberClassName: `${NUMBER_BASE_CLASS} sn-ethereal-text`,
      hairlineClassName: 'bg-gradient-to-r from-transparent via-violet-400/50 to-transparent',
      labelClassName: 'text-[11px] italic tracking-[0.22em] font-light text-violet-200',
      auraClassName:
        'absolute inset-0 rounded-2xl bg-[radial-gradient(ellipse_at_50%_70%,rgba(139,92,246,0.18),transparent_60%)] pointer-events-none',
    };
  }
  if (n <= 10) {
    return {
      label: 'Mythic',
      numberClassName: `${NUMBER_BASE_CLASS} text-amber-200`,
      hairlineClassName: 'bg-gradient-to-r from-transparent via-amber-400/45 to-transparent',
      labelClassName: 'text-[11px] italic tracking-[0.22em] font-light text-amber-200',
      auraClassName:
        'absolute inset-0 rounded-2xl bg-[radial-gradient(ellipse_at_50%_75%,rgba(251,191,36,0.10),transparent_55%)] pointer-events-none',
    };
  }
  if (n <= 100) {
    return {
      label: 'Ascendant',
      numberClassName: `${NUMBER_BASE_CLASS} text-[var(--color-accent)]`,
      hairlineClassName: 'bg-gradient-to-r from-transparent via-cyan-400/35 to-transparent',
      labelClassName: `${LABEL_BASE_CLASS} text-[var(--color-accent)]`,
    };
  }
  if (n <= 250) {
    return {
      label: 'Founder',
      numberClassName: `${NUMBER_BASE_CLASS} text-textPrimary`,
      hairlineClassName: 'bg-gradient-to-r from-transparent via-white/20 to-transparent',
      labelClassName: `${LABEL_BASE_CLASS} text-textPrimary`,
    };
  }
  return {
    label: 'Member',
    numberClassName: `${NUMBER_BASE_CLASS} text-textPrimary`,
    hairlineClassName: 'bg-gradient-to-r from-transparent via-white/15 to-transparent',
    labelClassName: `${LABEL_BASE_CLASS} text-textSecondary`,
  };
};

export const getTier = (n: number): TierInfo => {
  const base = getTierBase(n);
  const egg = EASTER_EGGS[n];
  if (!egg) return base;
  return typeof egg === 'string' ? { ...base, label: egg } : { ...base, ...egg };
};

const CARD_CLASS =
  'relative inline-flex flex-col items-center px-7 py-5 rounded-2xl min-w-[220px] ' +
  'bg-[rgba(14,14,18,0.92)] backdrop-blur-2xl border border-white/[0.04] ' +
  'shadow-[inset_1px_1px_0_0_rgba(255,255,255,0.14),inset_-1px_-1px_0_0_rgba(0,0,0,0.50),0_20px_50px_-15px_rgba(0,0,0,0.7),0_0_30px_-5px_rgba(0,0,0,0.4)]';

interface Props {
  userNumber: number;
  cosmeticName?: string | null;
}

export const StreamNookTierCard = memo(function StreamNookTierCard({ userNumber, cosmeticName = null }: Props) {
  const tier = getTier(userNumber);
  const numberText = String(userNumber);
  return (
    <div className={CARD_CLASS}>
      {tier.auraClassName && <div className={tier.auraClassName} />}
      <div className="relative flex flex-col items-center w-full">
        <div className="text-[9px] uppercase tracking-[0.36em] font-medium text-white/35 mb-4">
          StreamNook
        </div>

        {/* Number row: "Nº" prefix + the actual number, with a phantom Nº on
            the right (visibility: hidden) so the row is geometrically centered. */}
        <div className="flex items-baseline justify-center gap-2">
          <span className="text-[11px] text-white/30 font-light leading-none">Nº</span>
          <span className={tier.numberClassName}>{numberText}</span>
          <span className="text-[11px] font-light leading-none invisible" aria-hidden="true">Nº</span>
        </div>

        <div className={`w-16 h-px ${tier.hairlineClassName} mt-3.5 mb-2.5`} />
        {tier.label && <div className={tier.labelClassName}>{tier.label}</div>}

        {cosmeticName && (
          <div className="mt-3 flex flex-col items-center">
            <div className="text-[9px] uppercase tracking-[0.36em] font-medium text-white/35 mb-1.5">
              Badge
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-light text-white/80">
              {cosmeticName}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
